export type ProductPickerMode =
    | 'quick_search'
    | 'catalog_panel'
    | 'pos_scan'
    | 'tiles_catalog';

export type ProductPickerContext = 'orders' | 'operations' | 'purchases' | 'transfers';

export const PRODUCT_PICKER_CONTEXTS: ProductPickerContext[] = [
    'orders',
    'operations',
    'purchases',
    'transfers',
];

export const PRODUCT_PICKER_CONTEXT_META: Record<
    ProductPickerContext,
    { title: string; description: string; confirmAddLabel: string }
> = {
    orders: {
        title: 'Заявки',
        description: 'Создание и редактирование заявок',
        confirmAddLabel: 'Добавить в заявку',
    },
    operations: {
        title: 'Операции',
        description: 'Продажа / создание операции',
        confirmAddLabel: 'Добавить в операцию',
    },
    purchases: {
        title: 'Закупы',
        description: 'Создание приходных накладных',
        confirmAddLabel: 'Добавить в закуп',
    },
    transfers: {
        title: 'Переводы',
        description: 'Перемещение товаров между подразделениями',
        confirmAddLabel: 'Добавить в перевод',
    },
};

/** Fallback when context is unknown (preview without page, etc.) */
export const PRODUCT_PICKER_CONFIRM_ADD_FALLBACK = 'Добавить выбранные';

export const getProductPickerConfirmAddLabel = (
    context?: ProductPickerContext,
): string => (
    context
        ? PRODUCT_PICKER_CONTEXT_META[context].confirmAddLabel
        : PRODUCT_PICKER_CONFIRM_ADD_FALLBACK
);

export type ProductPickerModesMap = Partial<Record<ProductPickerContext, ProductPickerMode>>;

export const PRODUCT_PICKER_MODES: ProductPickerMode[] = [
    'quick_search',
    'catalog_panel',
    'pos_scan',
    'tiles_catalog',
];

export const PRODUCT_PICKER_MODE_META: Record<
    ProductPickerMode,
    { title: string; description: string; bestFor: string }
> = {
    quick_search: {
        title: 'Быстрый поиск',
        description: 'Строка поиска с умными подсказками. Enter добавляет товар в список.',
        bestFor: 'Опт, заявки по артикулу, менеджеры, которые знают названия',
    },
    catalog_panel: {
        title: 'Каталог-панель',
        description: 'Панель с поиском, фильтрами, превью и подробным списком. Можно выбрать несколько.',
        bestFor: 'Розница и менеджеры, которым нужно сравнивать позиции',
    },
    pos_scan: {
        title: 'Сканер / касса',
        description: 'Фокус всегда в поле ввода. Штрихкод, QR или артикул — мгновенно в список.',
        bestFor: 'Касса, склад, сканер-first сценарии',
    },
    tiles_catalog: {
        title: 'Плитки и категории',
        description: 'Крупные карточки для тач-экрана, корзина выбранных и изменение количества через − / +.',
        bestFor: 'Моноблоки, планшеты, визуальный выбор на кассе',
    },
};

export interface ProductPickerVariation {
    id: number;
    display_name: string;
    price?: number;
}

export interface ProductPickerItem {
    id: number;
    name: string;
    part_number?: string;
    brand?: string;
    description?: string;
    category_id?: number;
    category?: string;
    last_price?: number;
    set_price?: number;
    has_variation?: boolean;
    /** Product requires serial numbers (from products / business_stocks) */
    has_serial?: boolean;
    variations_combinations?: ProductPickerVariation[];
    images?: Array<{ url?: string } | string>;
    /** Stock qty for operations (from business_stocks) */
    quantity_in_hand?: number;
    measure_unit?: string;
    /** goods | service — affects labels/modes */
    entity_type?: 'goods' | 'service';
    /** Precomputed lowercase search string for client filtering */
    searchHaystack?: string;
    /** Raw source object for callers that need full product payload */
    raw?: any;
}

export interface ProductPickerCategory {
    id: number;
    name: string;
    count?: number;
}

export interface ProductPickResult {
    product: ProductPickerItem;
    variation?: ProductPickerVariation | null;
    quantity?: number;
    price?: number;
    qrWeight?: number;
    qrPrice?: number;
}

export interface ProductPickerProps {
    products: ProductPickerItem[] | any[];
    categories?: ProductPickerCategory[];
    mode?: ProductPickerMode;
    /** Page context for per-page owner setting / localStorage */
    context?: ProductPickerContext;
    /** goods | service — services skip scanner and use single pick */
    entityType?: 'goods' | 'service';
    /** Pick one item and close (default for services) */
    singlePick?: boolean;
    disabled?: boolean;
    loading?: boolean;
    placeholder?: string;
    onPick: (result: ProductPickResult) => void;
    /** Compact embed for table footer / replace cell */
    compact?: boolean;
    autoFocus?: boolean;
    /** Lazy-load full product (variations/images) when short catalog lacks details */
    fetchProductDetails?: (productId: number) => Promise<ProductPickerItem | null>;
    /** Always fetch full product before commit (purchases need measure/serial/pack fields) */
    forceProductDetails?: boolean;
}
